import { ref, readonly } from 'vue';
import axios from 'axios';

/**
 * Encapsule le pattern try/catch/finally répété dans tous les composables.
 *
 * Usage :
 *   const { isLoading, error, run } = useAsync();
 *   const data = await run(() => someService(), 'Message en cas d'erreur');
 *
 * - Les erreurs d'annulation Axios (AbortController) sont silencieuses (retourne null).
 * - `error` est toujours un string lisible, jamais un objet Error brut.
 * - Pour les soumissions de formulaire, renommer à la destructuration :
 *     const { isLoading: isSubmitting, run } = useAsync();
 */
export function useAsync() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  async function run<T>(fn: () => Promise<T>, errorMessage?: string): Promise<T | null> {
    isLoading.value = true;
    error.value = null;
    try {
      return await fn();
    } catch (err: unknown) {
      if (isCancelError(err)) return null;
      console.error(err);
      error.value = errorMessage ?? extractMessage(err);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    run,
  };
}

// ── Helpers internes ────────────────────────────────────────────────────────

function isCancelError(err: unknown): boolean {
  if (axios.isCancel(err)) return true;
  if (err instanceof Error && err.name === 'CanceledError') return true;
  if (err instanceof Error && (err as Error & { code?: string }).code === 'ERR_CANCELED')
    return true;
  return false;
}

function extractMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Une erreur est survenue';
}
